'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useMemo, useRef } from 'react';
import { useLenis } from 'lenis/react';
import { useContentSize } from '@/components/SizeProvider';
import GrabCursorLabel from '@/components/GrabCursorLabel';
import { useCollapseNavigate } from '@/lib/hooks/useCollapseNavigate';
import { useDraggable } from '@/lib/hooks/useDraggable';
import { useVideoArming } from '@/lib/hooks/useVideoArming';
import { useZIndexManager } from '@/lib/hooks/useZIndexManager';
import type { BlogPostModel } from '@/lib/models/BlogPost';
import {
  BLOG_THUMBNAIL_WIDTH,
  BLOG_THUMBNAIL_HEIGHT,
  BLOG_THUMBNAIL_ASPECT_RATIO,
} from '@/lib/constants/media';
import { getBodyFontSize, getTitleFontSize } from '@/lib/utils/typography';
import styles from './blog.module.scss';

interface Props {
  posts: BlogPostModel[];
}

interface CardProps {
  post: BlogPostModel;
  initialPosition: { x: number; y: number };
  size: { width: number; height: number };
  pageHeight: number;
  zIndex: number;
  cardFontSize: number;
  isCollapsing: boolean;
  onPointerDownCapture: () => void;
  onNavigate: (href: string) => void;
  eager?: boolean;
}

const COLLAPSE_DURATION_MS = 300;
const REFERENCE_CONTENT_WIDTH = 1920;
const REFERENCE_CONTENT_HEIGHT = 960;
const CENTER_WIDTH_PER_HEIGHT = 16 / 15;
const HORIZONTAL_CARD_GAP = 40;
const VERTICAL_CARD_GAP = 60;
const FIRST_ROW_TOP_RATIO = 0.22;
const PAGE_BOTTOM_BUFFER_RATIO = 0.1;

const CARD_TITLE_LINE_HEIGHT = 1.1;
const CARD_TITLE_PADDING_Y = 24;
const CARD_EXCERPT_LINE_HEIGHT = 1.1;
const CARD_EXCERPT_LINE_CLAMP = 3;
const CARD_EXCERPT_MARGIN_BOTTOM = 10;
const CARD_BUTTON_LINE_HEIGHT = 1.1;
const CARD_BUTTON_PADDING_Y = 18;
const CARD_BUTTON_BORDER_Y = 4;
const CARD_BOTTOM_PADDING_Y = 26;
const BLOG_ICON_INTRINSIC_WIDTH = 27;
const BLOG_ICON_INTRINSIC_HEIGHT = 24;
const LAB_NOTES_FONT_DIVISOR = 90;

const HERO_REFERENCE_POSITION = {
  x: 440,
  y: 96,
};

const CARD_RIGHT_BUFFER = 56;

const CARD_POSITION_OFFSETS: Array<{ dx: number; dy: number }> = [
  { dx: 55, dy: -60 },
  { dx: -65, dy: 35 },
  { dx: 30, dy: 65 },
  { dx: -45, dy: -50 },
  { dx: 70, dy: 25 },
  { dx: -35, dy: -70 },
  { dx: 40, dy: 55 },
  { dx: -60, dy: 15 },
];

function BlogCard({
  post,
  initialPosition,
  size,
  pageHeight,
  zIndex,
  cardFontSize,
  isCollapsing,
  onPointerDownCapture,
  onNavigate,
  eager = false,
}: CardProps) {
  const { position, isPointerDown, onPointerDown } = useDraggable({
    initialPosition,
    allowDragging: true,
    size,
    isOnLandingPage: false,
    constrainingFunction: (pos, elementSize) => {
      const constrainedX = Math.max(
        -elementSize.width / 2,
        Math.min(pos.x, window.innerWidth - elementSize.width / 2),
      );
      const maxY = pageHeight > 0 ? pageHeight - elementSize.height / 2 : pos.y;
      const constrainedY = Math.max(0, Math.min(pos.y, maxY));
      return { x: constrainedX, y: constrainedY };
    },
  });

  const videoRef = useRef<HTMLVideoElement>(null);
  const isVideo = post.thumbnail?.endsWith('.mp4') ?? false;
  const { shouldLoadVideo } = useVideoArming({
    videoRef,
    initialLoad: isVideo,
    initialPlay: isVideo,
  });

  const href = `/blog/${post.slug}`;

  return (
    <article
      className={`${styles.blogCardWrapper} ${isCollapsing ? styles.collapseWindow : styles.expandWindow}`}
      style={{
        left: position.x,
        top: position.y,
        width: size.width,
        zIndex,
      }}
    >
      <div
        className={styles.blogCard}
        data-grabbable=""
        style={{
          cursor: isPointerDown ? 'grabbing' : 'grab',
        }}
        onPointerDown={(event) => {
          onPointerDownCapture();
          onPointerDown(event);
        }}
      >
        <h2 className={styles.cardTitle} style={{ fontSize: cardFontSize }}>
          <img
            className={styles.cardIcon}
            src="/assets/image/BlogHeaderIcon.svg"
            alt=""
            width={BLOG_ICON_INTRINSIC_WIDTH}
            height={BLOG_ICON_INTRINSIC_HEIGHT}
            aria-hidden="true"
          />
          {post.title.toUpperCase()}
        </h2>
        <div
          className={styles.cardMedia}
          style={{ height: Math.ceil(size.width * BLOG_THUMBNAIL_ASPECT_RATIO) }}
        >
          {post.thumbnail ? (
            isVideo ? (
              <video
                ref={videoRef}
                className={styles.cardImage}
                src={shouldLoadVideo ? post.thumbnail : undefined}
                preload={shouldLoadVideo ? 'metadata' : 'none'}
                muted
                playsInline
                loop
                autoPlay
                aria-label={`${post.title} video`}
                style={{ aspectRatio: `${BLOG_THUMBNAIL_WIDTH} / ${BLOG_THUMBNAIL_HEIGHT}` }}
              >
                <track
                  kind="captions"
                  src="/captions/no-audio.vtt"
                  srcLang="en"
                  label="No spoken audio"
                />
              </video>
            ) : (
              <Image
                className={styles.cardImage}
                src={post.thumbnail}
                alt={post.title}
                width={BLOG_THUMBNAIL_WIDTH}
                height={BLOG_THUMBNAIL_HEIGHT}
                sizes="30vw"
                unoptimized={post.thumbnail.endsWith('.gif')}
                loading={eager ? 'eager' : undefined}
              />
            )
          ) : (
            <div className={styles.cardImageFallback} aria-hidden="true" />
          )}
        </div>
        <div className={styles.cardBottom}>
          <p className={styles.cardExcerpt} style={{ fontSize: cardFontSize }}>
            {post.excerpt}
          </p>
          <Link
            href={href}
            className={styles.readMoreButton}
            style={{ fontSize: cardFontSize }}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.preventDefault();
              onNavigate(href);
            }}
          >
            READ MORE
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function BlogIndexContent({ posts }: Props) {
  const { width: widthContent, height: heightContent } = useContentSize();
  const { getZIndex, handleElementClick } = useZIndexManager({ wrapAround: 50 });
  const { collapsingKey, navigate } = useCollapseNavigate(COLLAPSE_DURATION_MS);
  const lenis = useLenis();

  // This component only renders on /blog, so the breadcrumb's BLOG link is
  // always same-route: re-navigating would remount and reshuffle the cards,
  // so cancel it and scroll to the top instead.
  const _onBlogBreadcrumbClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (lenis) lenis.scrollTo(0);
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cardFontSize = getBodyFontSize(widthContent);
  const heroIntroFontSize = getTitleFontSize(widthContent);
  const labNotesFontSize = Math.ceil(widthContent / LAB_NOTES_FONT_DIVISOR);

  const centerWidth = heightContent * CENTER_WIDTH_PER_HEIGHT;
  const centerLeft = (widthContent - centerWidth) / 2;
  const cardWidth = Math.max(
    0,
    Math.floor((centerWidth - HORIZONTAL_CARD_GAP) / 2),
  );
  const cardMediaHeight = Math.ceil(cardWidth * BLOG_THUMBNAIL_ASPECT_RATIO);

  const cardTitleHeight =
    Math.ceil(cardFontSize * CARD_TITLE_LINE_HEIGHT) + CARD_TITLE_PADDING_Y;
  const cardExcerptHeight = Math.ceil(
    cardFontSize * CARD_EXCERPT_LINE_HEIGHT * CARD_EXCERPT_LINE_CLAMP,
  );
  const cardButtonHeight =
    Math.ceil(cardFontSize * CARD_BUTTON_LINE_HEIGHT) +
    CARD_BUTTON_PADDING_Y +
    CARD_BUTTON_BORDER_Y;
  const cardBottomHeight =
    CARD_BOTTOM_PADDING_Y +
    cardExcerptHeight +
    CARD_EXCERPT_MARGIN_BOTTOM +
    cardButtonHeight;

  const cardSize = {
    width: cardWidth,
    height: cardMediaHeight + cardTitleHeight + cardBottomHeight,
  };

  const randomOffsets = useMemo(
    () => posts.map(() => CARD_POSITION_OFFSETS[Math.floor(Math.random() * CARD_POSITION_OFFSETS.length)]),
    [posts],
  );

  const positions = useMemo(() => {
    if (widthContent <= 0 || heightContent <= 0 || cardWidth <= 0) return [];

    const leftOffset = (window.innerWidth - widthContent) / 2;
    const topOffset = (window.innerHeight - heightContent) / 2;

    const firstRowY = topOffset + heightContent * FIRST_ROW_TOP_RATIO;
    const halfCardHeight = cardSize.height / 2;
    const rowPitch = cardSize.height + VERTICAL_CARD_GAP;

    const leftColumnX = leftOffset + centerLeft;
    const rightColumnX = leftOffset + centerLeft + cardWidth + HORIZONTAL_CARD_GAP;

    const leftBound = leftColumnX;
    const rightBound = leftColumnX + centerWidth - CARD_RIGHT_BUFFER;

    return posts.map((_, index) => {
      const column = index % 2;
      const row = Math.floor(index / 2);

      const baseX = column === 0 ? leftColumnX : rightColumnX;
      const baseY = firstRowY + row * rowPitch + (column === 1 ? halfCardHeight : 0);

      const offset = randomOffsets[index] ?? { dx: 0, dy: 0 };
      const dy = index === 0 ? Math.max(0, offset.dy) : offset.dy;
      const clampedX = Math.max(leftBound, Math.min(baseX + offset.dx, rightBound - cardSize.width));

      return { x: clampedX, y: baseY + dy };
    });
  }, [cardSize.height, cardSize.width, cardWidth, centerLeft, centerWidth, heightContent, posts, randomOffsets, widthContent]);

  const pageHeightPx = useMemo(() => {
    if (positions.length === 0) return 0;

    const tallest = positions.reduce((max, pos) => {
      return Math.max(max, pos.y + cardSize.height);
    }, 0);

    return tallest + window.innerHeight * PAGE_BOTTOM_BUFFER_RATIO;
  }, [cardSize.height, positions]);

  const pageHeight = pageHeightPx > 0 ? `${pageHeightPx}px` : '100vh';

  const isMeasured = widthContent > 0 && heightContent > 0;
  const heroLeft = isMeasured
    ? (window.innerWidth - widthContent) / 2 + (HERO_REFERENCE_POSITION.x / REFERENCE_CONTENT_WIDTH) * widthContent
    : undefined;
  const centerRightEdge = isMeasured
    ? (window.innerWidth - widthContent) / 2 + centerLeft + centerWidth
    : undefined;
  const heroWidth = isMeasured && heroLeft !== undefined && centerRightEdge !== undefined
    ? Math.max(0, centerRightEdge - heroLeft)
    : undefined;
  const heroTop = isMeasured
    ? (window.innerHeight - heightContent) / 2 + heightContent * (HERO_REFERENCE_POSITION.y / REFERENCE_CONTENT_HEIGHT)
    : undefined;

  return (
    <main className={styles.blogIndex} style={{ minHeight: pageHeight }}>
      <section className={styles.blogHero} style={{ left: heroLeft, top: heroTop, width: heroWidth }}>
        <p className={styles.breadcrumb} style={{ fontSize: cardFontSize }}>
          <Link href="/" className={styles.breadcrumbLink}>HOME</Link>
          {' -> '}
          <Link href="/blog" onClick={_onBlogBreadcrumbClick} className={styles.breadcrumbLink}>BLOG [UNSTABLE FINDINGS]</Link>
        </p>
        <div className={styles.heroRow}>
          <h1 className={styles.pageTitle} style={{ fontSize: labNotesFontSize }}>
            <img
              className={styles.blogIcon}
              src="/assets/image/BlogHeaderIcon.svg"
              alt=""
              width={BLOG_ICON_INTRINSIC_WIDTH}
              height={BLOG_ICON_INTRINSIC_HEIGHT}
              aria-hidden="true"
            />
            LAB NOTES
          </h1>
          <p
            className={styles.heroIntro}
            style={{ fontSize: heroIntroFontSize }}
          >
            NOTES FROM THE LAB.<br />
            HALF EXPERIMENTS, HALF ACCIDENTS.<br />
            SOME IDEAS ESCAPED CONTAINMENT.
          </p>
        </div>
      </section>
      {posts.map((post, index) => (
        <BlogCard
          key={post.id}
          post={post}
          initialPosition={positions[index] ?? { x: 0, y: 0 }}
          size={cardSize}
          pageHeight={pageHeightPx}
          zIndex={getZIndex(`blog-${post.id}`)}
          cardFontSize={cardFontSize}
          isCollapsing={collapsingKey !== null}
          onPointerDownCapture={() => handleElementClick(`blog-${post.id}`)}
          onNavigate={(href) => navigate(href, post.slug)}
          eager={index === 0}
        />
      ))}
      <GrabCursorLabel />
    </main>
  );
}
